/**
 * Phase 6 cross-cutting PR 5 — `<StreakChip>`.
 *
 * Ultra (≥ 1920 px) LeftAux on `/v2/today`: surfaces the current
 * consecutive-completed-day streak prominently in the chrome.
 * Complements the mobile `StreakHeat` MiniTile (which lives in the
 * main column on mobile / packed-default) by giving Ultra a leaderboard
 * style chip that's always visible without scrolling.
 *
 * Sourced from:
 *   - HANDOFF.md PR 5 acceptance: "Ultra `/v2/today` LeftAux mounts a
 *     <StreakChip> showing current streak + personal best (read-only)."
 *   - direction.md §4 viewport row: "Ultra 1920 ... + leaderboard-style
 *     streak chip in the LeftAux (🔥 12-day streak · personal best 23)"
 *
 * Wire-up:
 *   - Caller (today.tsx) computes the streak via PR 4e's
 *     `computeCurrentStreak(seasonPlans.data)` and passes it in.
 *   - Personal best is `Math.max(currentStreak, _streak_personal_best)`
 *     (caller-derived). Until the schema migration adds a stored PB,
 *     the displayed PB equals current streak (always ≥ current). The
 *     chip then reads "🔥 12-day streak" without the "· personal
 *     best N" suffix to avoid showing the same number twice.
 *
 * Self-gates on viewport via CSS @media (≥ 1920); below Ultra the
 * component returns nothing visually. The component still mounts —
 * keeping the DOM stable across viewport changes avoids layout shift
 * when a window is resized across the Ultra boundary.
 *
 * Zero-state: when `currentStreak === 0`, the chip returns null
 * (no "0-day streak" copy — would scan as a failure state, not
 * useful chrome).
 */

import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './StreakChip.module.css';

export interface StreakChipProps {
  /** Current consecutive-completed-day streak. Zero hides the chip. */
  currentStreak: number;
  /** Personal best streak (from athlete_context). When equal to
   *  current, the suffix is suppressed to avoid duplicate display. */
  personalBest?: number | null;
}

export function StreakChip({ currentStreak, personalBest }: StreakChipProps) {
  // ⚠️ Read BEFORE the zero-state early return — a hook after a conditional
  // return is a rules-of-hooks violation, and this component genuinely does
  // return null for `currentStreak === 0`.
  const iconsOn = useIconSystem();
  if (currentStreak <= 0) return null;

  const showPb =
    typeof personalBest === 'number' &&
    personalBest > 0 &&
    personalBest > currentStreak;

  return (
    <div className={styles.chip} aria-label={`${currentStreak}-day current training streak`}>
      {/* CD-ICONS-01 — `chip.streak` @16 (README §Migration Map). It aliases
          `kudo.fire`: one flame family across streaks and kudos. Decorative —
          the chip's own `aria-label` already reads "N-day current training
          streak". Flag OFF renders the untouched 🔥 text node. */}
      <span className={styles.flame} aria-hidden="true">
        {iconsOn ? <Icon name="chip.streak" size={16} /> : '🔥'}
      </span>
      <span className={styles.count}>
        <span className={styles.countNum}>{currentStreak}</span>
        <span className={styles.countUnit}>-day streak</span>
      </span>
      {showPb ? (
        <>
          <span className={styles.sep} aria-hidden="true">·</span>
          <span className={styles.pb}>
            personal best <span className={styles.pbNum}>{personalBest}</span>
          </span>
        </>
      ) : null}
    </div>
  );
}
